import { Suspense, lazy, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary'

import style from './styles.module.scss'

const OedPDFViewer = lazy(() => import('pdf_viewer/OedPDFViewer'))

export const Home = () => {
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const pdftronExtensions = ['pdf', 'xod']
  const extensionsNotForIframe = ['swf', 'mp4', 'mp3', 'wav', ...pdftronExtensions]

  const getParameterByName = (name: string) => {
    const queryParams = new URLSearchParams(window.location.search)
    const queryValue = queryParams.get(name) || ''

    if (queryValue) {
      window.sessionStorage.setItem(name, queryValue)
      return queryValue
    }

    const sessionValue = window.sessionStorage.getItem(name)
    return sessionValue || ''
  }

  const getVimeoUrl = (urlOed: string) => {
    if (urlOed.includes('openvimeo.html') && urlOed.includes('video_url')) {
      const urlParts = urlOed.split('openvimeo.html')
      const vimeoSearchParams = urlParts[1]
      const queryParams = new URLSearchParams(vimeoSearchParams)
      const vimeoUrl = queryParams.get('video_url')
      const urlPartsVimeo = vimeoSearchParams.split(`${vimeoUrl}`)
      const newUrl = vimeoUrl + urlPartsVimeo[1]
      return newUrl
    }
    return ''
  }

  const urlOed = getParameterByName('url')
  const vimeoUrl = getVimeoUrl(urlOed)

  const urlHasQueryParams = urlOed?.includes('?')
  const urlWithoutQueryParams = urlHasQueryParams ? urlOed.split('?').shift() : urlOed
  const extension = urlWithoutQueryParams?.split('.').pop() || ''

  useEffect(() => {
    const environment = window.location.href.includes('.s.plurall.net') ? 'stg' : 'prod'
    const token = getParameterByName('token')?.replace('Bearer ', '')

    if (!urlOed?.includes('plurall.discoveryeducation.com') && token) {
      document.cookie = `${
        environment === 'stg' ? 'staging_' : ''
      }shared_cookie=${getParameterByName('token').replace('Bearer ', '')};domain=${
        environment === 'stg' ? '.s' : ''
      }.plurall.net;path=/`
    }

    if (
      urlOed?.includes('plurall-content') ||
      (urlOed?.includes('app.plurall.net') && pdftronExtensions.includes(extension)) ||
      extensionsNotForIframe.slice(0, 3).includes(extension) ||
      extensionsNotForIframe.includes(extension) ||
      vimeoUrl
    ) {
      setLoading(false)
    } else if (urlOed && !vimeoUrl) {
      const newUrlOed = urlOed.replace('openvimeo.html', '')
      const newUrl = newUrlOed
      if (/[http|https]:\/\//.test(newUrl)) {
        window.location.href = newUrl
      } else {
        window.location.href = `http://${newUrl}`
      }
    }
    navigate(`${window.location.pathname}# `, { replace: true })
  }, [])

  // eslint-disable-next-line react/no-unstable-nested-components
  const LoadingScreen = () => (
    <div className={style['div-loader']}>
      <div className={style.loader} />
    </div>
  )

  const childElementRenderer = () => {
    if (loading) {
      return <LoadingScreen />
    }

    if (!extensionsNotForIframe.includes(extension)) {
      return (
        <iframe
          title='Default viewer'
          src={vimeoUrl || urlOed}
          className={style.view}
          frameBorder='0'
        />
      )
    }

    if (pdftronExtensions.includes(extension)) {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = 'https://assets.cdn.plurall.net/plurall-tracker/tracker.js'
      script.onload = () => {
        const today = new Date()
        const initialDay = new Date('09/13/2023')
        const TRIAL_DAYS = 60
        const SECONDS_PER_DAY = 1000 * 60 * 60 * 24
        const daysDiffDate =
          ((today as unknown as number) - (initialDay as unknown as number)) / SECONDS_PER_DAY

        if (daysDiffDate < TRIAL_DAYS) {
          let environment = process.env.REACT_APP_NODE_ENV
          if (environment === 'pull_request' || environment === 'local') {
            environment = 'staging'
          }

          window.PLURALL_TRACKER = new window.PlurallTracker({
            clientId: 'oed',
            environment,
          })

          window.PLURALL_TRACKER.pageView()
        }
      }
      document.head.appendChild(script)

      const isXOD = extension === 'xod'

      return (
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen />}>
            <OedPDFViewer width='100%' fileSrc={urlOed} isXOD={isXOD} />
          </Suspense>
        </ErrorBoundary>
      )
    }

    if (extension === 'swf') {
      return (
        <object width='100%' height='550'>
          <param name='movie' value={urlOed} />
          <embed src={urlOed} className={style.view} />
        </object>
      )
    }

    if (extension === 'mp4') {
      return (
        <video
          width='100%'
          height='550'
          controls
          controlsList='nodownload'
          preload='auto'
          autoPlay
          muted
        >
          <source src={urlOed} type='video/mp4' />
        </video>
      )
    }

    if (extension === 'mp3' || extension === 'wav') {
      return (
        <audio controls controlsList='nodownload' autoPlay loop>
          <track kind='captions' src='' />
          <source src={urlOed} type='audio/mp3' />
        </audio>
      )
    }

    return <span>Nenhuma reprodução disponível encontrada</span>
  }

  return <div className={style['oed-container']}>{childElementRenderer()}</div>
}
